import React, { Fragment, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { appRoutes } from '../../constants';
import { useRouter, hasAccess } from "utils";
import {logoutUser} from "../../actions/User";

/**
 * Lets a page in, or sends the user to the 401 page.
 *
 * It decides only once the session is CHECKED: the roles bootstrapped from a
 * stored JWT are a login-time snapshot, and /api/me (SessionRefresh) may
 * still be about to add one. Deciding earlier sent a super admin whose role
 * was granted after login to the 401 page whenever /audit-log was opened
 * directly — from the menu it worked, because by then the refresh had landed.
 *
 * Until it has decided, and when it has refused, the page is NOT rendered:
 * a page mounted only to be redirected away still fires its requests, each
 * answered 403.
 *
 * NOT AUTHENTICATED is the exception, and deliberately so: an expired or
 * broken token still renders the page, whose requests then 401 and produce
 * the ONE «session expired» notice and the redirect to login
 * (errorRequestHandler; session-expiration.admin.spec). Rendering nothing
 * there logged the user out without a word.
 */
const AuthGuard = props => {
  const { roles, children } = props;
  const dispatch = useDispatch();
  const session = useSelector(state => state.userSession);
  const {user, isAuthenticated, isSessionChecked = true} = session;
  const router = useRouter();

  const allowed = !!(isAuthenticated && user && hasAccess(user.roles, roles));

  useEffect(() => {
    if (!isAuthenticated || !user) {
      dispatch(logoutUser());
      return;
    }

    if (isSessionChecked && !allowed) {
      router.history.push(appRoutes.ERROR_PAGE_401);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router, isSessionChecked, allowed]);

  if (isAuthenticated && (!isSessionChecked || !allowed)) {
    return null;
  }

  return <Fragment>{children}</Fragment>;
};

AuthGuard.propTypes = {
  children: PropTypes.node,
  roles: PropTypes.array.isRequired
};

AuthGuard.defaultProps = {
  roles: []
};

export default AuthGuard;
